<template>
  <RequiresPermission
    permission="manage_notifications"
    fallback-route-name="merchantIndex"
  >
    <div>
      <div class="mb-3 flex justify-end">
        <RouterLink
          #default="{ href, navigate }"
          :to="{ name: 'notificationCreate' }"
          custom
        >
          <ActionButton
            as="a"
            :href="href"
            @click="navigate"
          >
            Create Notification
          </ActionButton>
        </RouterLink>
      </div>

      <BaseCard class="relative overflow-hidden">
        <QueryLoading v-if="isLoading" class="min-h-72" />
        <QueryError
          v-else-if="isNotificationsError"
          class="min-h-72"
          :show-retry="true"
          @retry="refetchNotifications"
        >
          Failed to retrieve notifications
        </QueryError>
        <template v-else>
          <BaseTable
            v-model:ordering="ordering"
            :rows="data?.items ?? []"
            :columns="columns"
            :is-loading="isFetching"
            :get-row-class="getRowClass"
            class="overflow-auto"
          >
            <template #cell-createdAt="{ value, row }">
              <div class="mb-1">
                {{ value }}
              </div>

              <div>
                <span
                  v-if="!row.isActive"
                  class="rounded-full text-error"
                >
                  Deleted
                </span>

                <span
                  v-else-if="notificationIsUpcoming(row)"
                  class="rounded-full text-info"
                >
                  Upcoming
                </span>

                <span
                  v-else-if="notificationIsExpired(row)"
                  class="rounded-full text-error"
                >
                  Expired
                </span>
              </div>
            </template>

            <template #cell-contents="{ value, row }">
              <div class="flex flex-col items-center">
                <NotificationPreview :contents="value" />
                <ButtonPrimary
                  @click="onCopyNotificationContent(row)"
                >
                  <template v-if="copiedIds.has(row.id)">
                    <IconCheckCircle class="mr-1" />
                    Copied
                  </template>
                  <template v-else>
                    Copy to clipboard
                  </template>
                </ButtonPrimary>
              </div>
            </template>

            <template #cell-viewCounts="{ row }">
              <div
                class="grid min-w-[4.5rem] text-base text-background-fg/70 lg:grid-cols-2"
              >
                <div
                  class="flex items-center"
                  :class="
                    row.sendToTerminal ? '' : 'opacity-30'
                  "
                >
                  <div
                    class="mr-1 size-5 shrink-0"
                    :class="
                      row.sendToTerminal
                        ? ''
                        : 'slashthrough'
                    "
                  >
                    <IconTerminal class="size-full" />
                  </div>
                  <span v-if="row.sendToTerminal">
                    {{ row.terminalViewCount }}
                  </span>
                </div>
                <div
                  class="flex items-center"
                  :class="
                    row.sendToPortal ? '' : 'opacity-30'
                  "
                >
                  <div
                    class="mr-1 size-5 shrink-0"
                    :class="
                      row.sendToPortal ? '' : 'slashthrough'
                    "
                  >
                    <IconWeb class="size-full" />
                  </div>
                  <span
                    v-if="row.sendToPortal"
                    class="ml-[2px]"
                  >
                    {{ row.portalViewCount }}
                  </span>
                </div>
              </div>
            </template>

            <template #cell-actions="{ row }">
              <BaseTableButton
                v-if="canDeleteNotification(row)"
                variant="danger"
                @click="onDelete(row.id)"
              >
                Delete
              </BaseTableButton>
            </template>

            <template #cell-untilDatetime="{ value }">
              <p>
                {{ formatToDate(value) }}
                {{ formatToTime(value) }}
              </p>
              <p class="text-sm text-background-fg/50">
                {{
                  DateFns.formatDistance(value, now, {
                    addSuffix: true,
                  })
                }}
              </p>
            </template>

            <template #cell-fromDatetime="{ value }">
              <p>
                {{ formatToDate(value) }}
                {{ formatToTime(value) }}
              </p>
              <p class="text-sm text-background-fg/50">
                {{
                  DateFns.formatDistance(value, now, {
                    addSuffix: true,
                  })
                }}
              </p>
            </template>
          </BaseTable>

          <div class="flex py-2">
            <BasePagination
              v-model="page"
              :page-count="pageCount"
              class="ml-auto"
            />
          </div>
        </template>
      </BaseCard>
    </div>

    <BaseConfirmation
      ref="deleteNotificationConfirmationRef"
      title="Delete Notification"
      description="Are you sure you want to delete this notification?"
      confirm-label="Delete"
      cancel-label="No"
    />
  </RequiresPermission>
</template>

<script setup lang="ts">
import { useRequiredPermissions } from "@/composables/useRequiredPermissions"
import type {
  TableColumn,
  TableOrdering,
} from "@/components/BaseTable.vue"
import type {
  ListNotificationsResponseItem,
  ListNotificationsOrdering,
} from "@/services/notifications/listNotifications"

const { hasRequiredPermissions } = useRequiredPermissions(
  "manage_notifications"
)
const { page, pageCount, pagination } = usePagination({
  count: computed(() => data.value?.count || 0),
  limit: 10,
})

const ordering = ref<
  TableOrdering<ListNotificationsOrdering> | undefined
>()

const {
  data,
  isLoading,
  isFetching,
  isError: isNotificationsError,
  refetch: refetchNotifications,
} = useNotifications(
  computed(() => ({
    ...pagination.value,
    ordering: ordering.value,
  })),
  hasRequiredPermissions
)
const now = useNow()

type ColumnId =
  | "createdAt"
  | "contents"
  | "fromDatetime"
  | "untilDatetime"
  | "viewCounts"
  | "actions"
const columns: Array<
  TableColumn<
    ListNotificationsResponseItem,
    ColumnId,
    ListNotificationsOrdering
  >
> = [
  {
    id: "createdAt",
    header: "Created At",
    getCellValue: (row) => formatToDate(row.createdAt),
    autoWidth: true,
    isSortable: true,
  },
  {
    id: "contents",
    header: "Contents",
    getCellValue: (row) => row.contents,
    align: "center",
    desktopOnly: true,
  },
  {
    id: "fromDatetime",
    header: "From",
    getCellValue: (row) => row.fromDatetime,
    autoWidth: true,
    isSortable: true,
  },
  {
    id: "untilDatetime",
    header: "Until",
    getCellValue: (row) => row.untilDatetime,
    autoWidth: true,
    isSortable: true,
  },
  {
    id: "viewCounts",
    header: "Viewed",
  },
  {
    id: "actions",
    header: "",
    autoWidth: true,
  },
]

const getRowClass = (row: ListNotificationsResponseItem) =>
  !row.isActive || notificationIsExpired(row)
    ? "opacity-50"
    : ""

const deleteNotificationConfirmationRef = useTemplateRef(
  "deleteNotificationConfirmationRef"
)

const onDelete = async (
  notificationId: ListNotificationsResponseItem["id"]
) => {
  if (deleteNotificationConfirmationRef.value === null)
    return

  const { isCanceled } =
    await deleteNotificationConfirmationRef.value.reveal()

  if (isCanceled) return

  deleteNotification(notificationId)
}

const copiedIds = ref<Set<string>>(new Set())
const onCopyNotificationContent = (
  notification: ListNotificationsResponseItem
) => {
  navigator.clipboard.writeText(notification.contents)

  copiedIds.value.add(notification.id)
  setTimeout(
    () => copiedIds.value.delete(notification.id),
    4000
  )
}

const canDeleteNotification = (
  notification: ListNotificationsResponseItem
) => {
  return (
    notification.isActive &&
    DateFns.isAfter(notification.untilDatetime, new Date())
  )
}

const notificationIsUpcoming = (
  Notification: ListNotificationsResponseItem
) => DateFns.isAfter(Notification.fromDatetime, new Date())
const notificationIsExpired = (
  Notification: ListNotificationsResponseItem
) =>
  DateFns.isBefore(Notification.untilDatetime, new Date())
</script>

<style lang="scss" scoped>
.slashthrough {
  position: relative;

  &::before {
    position: absolute;
    content: "";
    top: 45%;
    left: 0;
    right: 0;
    width: 100%;
    height: 2px;
    background: black;

    -webkit-transform: rotate(-25deg);
    -moz-transform: rotate(-25deg);
    -ms-transform: rotate(-25deg);
    -o-transform: rotate(-25deg);
    transform: rotate(-25deg);
  }
}
</style>
